import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BookOpen, ClipboardList, PackageCheck, Receipt, Shirt, ShoppingBag, Truck, Wallet, type LucideIcon } from "lucide-react";

import { CataloguePanel, HistoryPanel, OrdersPanel, PaymentsPanel, StockPanel, SuppliersPanel } from "../components/uniforms/UniformPanels";
import { ugx } from "../components/uniforms/uniformLogic";
import { usePermissions } from "../hooks/usePermissions";
import { uniformService } from "../services/uniformService";

type Tab = "shop" | "orders" | "stock" | "history" | "suppliers" | "payments";

/**
 * Uniforms. The uniform office confirms family orders (setting the stock
 * aside), packs them and hands them over once the bursar has the money, and
 * keeps the stock book and suppliers. Families browse the shop, order for
 * their children and see what they have paid.
 */
export default function Uniforms() {
  const { hasCapability } = usePermissions();
  const office = hasCapability("access.uniforms.uniform_manager");
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") ?? undefined;

  const stats = useQuery({ queryKey: ["uniforms", "stats"], queryFn: uniformService.getStats });
  const items = useQuery({ queryKey: ["uniforms", "items"], queryFn: uniformService.getItems });
  const orders = useQuery({ queryKey: ["uniforms", "orders"], queryFn: uniformService.getOrders });
  const payments = useQuery({ queryKey: ["uniforms", "payments"], queryFn: uniformService.getPayments });
  const categories = useQuery({ queryKey: ["uniforms", "categories"], queryFn: uniformService.getCategories, enabled: office });
  const suppliers = useQuery({ queryKey: ["uniforms", "suppliers"], queryFn: uniformService.getSuppliers, enabled: office });
  const moves = useQuery({ queryKey: ["uniforms", "moves"], queryFn: uniformService.getStockMoves, enabled: office });
  const learners = useQuery({ queryKey: ["uniforms", "learners"], queryFn: uniformService.getStudents, staleTime: 5 * 60_000 });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["uniforms"] });
  const setup = { learners: learners.data ?? [], items: items.data ?? [], categories: categories.data ?? [], suppliers: suppliers.data ?? [] };

  const s = stats.data;
  const tabs: { id: Tab; label: string; icon: LucideIcon; badge?: number }[] = office
    ? [
        { id: "orders", label: "Orders", icon: ClipboardList, badge: s?.pending_orders },
        { id: "stock", label: "Stock", icon: Shirt, badge: (s?.out_of_stock_items ?? 0) + (s?.low_stock_items ?? 0) },
        { id: "history", label: "Stock book", icon: BookOpen },
        { id: "suppliers", label: "Suppliers", icon: Truck },
        { id: "payments", label: "Payments", icon: Receipt },
      ]
    : [
        { id: "shop", label: "Shop", icon: ShoppingBag },
        { id: "orders", label: "Orders", icon: ClipboardList, badge: s?.ready_orders },
        { id: "payments", label: "Payments", icon: Receipt },
      ];
  const fallback: Tab = office ? "orders" : "shop";
  const tab: Tab = tabs.find((item) => item.id === params.get("tab"))?.id ?? fallback;
  const go = (next: Tab, extra: Record<string, string> = {}) => {
    const query: Record<string, string> = { ...extra };
    if (next !== fallback) query.tab = next;
    setParams(query, { replace: true });
  };
  const dash = (value?: number) => (stats.isLoading || value === undefined ? "–" : String(value));
  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = office
    ? [
        { label: "To confirm", value: dash(s?.pending_orders), note: `${s?.in_preparation ?? 0} being prepared`, go: () => go("orders", { view: "pending" }), icon: ClipboardList, alert: Boolean(s?.pending_orders) },
        { label: "Ready to collect", value: dash(s?.ready_orders), note: `${s?.ready_paid ?? 0} paid and can be handed over`, go: () => go("orders", { view: "ready" }), icon: PackageCheck },
        { label: "Owed on open orders", value: stats.isLoading ? "–" : ugx(s?.balance_due), note: `${s?.orders_owing ?? 0} ${s?.orders_owing === 1 ? "order" : "orders"} · paid at the bursar's office`, go: () => go("orders", { view: "owing" }), icon: Wallet },
        { label: "Out of stock", value: dash(s?.out_of_stock_items), note: `${s?.low_stock_items ?? 0} more running low · stock worth ${ugx(s?.stock_value)}`, go: () => go("stock", { view: s?.out_of_stock_items ? "out" : "low" }), icon: AlertTriangle, alert: Boolean(s?.out_of_stock_items) },
      ]
    : [
        { label: "Open orders", value: dash(s?.open_orders), note: "Ordered, not yet collected", go: () => go("orders"), icon: ClipboardList },
        { label: "To pay", value: stats.isLoading ? "–" : ugx(s?.balance_due), note: s?.orders_owing ? "At the bursar's office" : "Nothing owing", go: () => go("orders", { view: "owing" }), icon: Wallet, alert: Boolean(s?.orders_owing) },
        { label: "Ready to collect", value: dash(s?.ready_orders), note: "From the uniform office", go: () => go("orders", { view: "ready" }), icon: PackageCheck, alert: Boolean(s?.ready_orders) },
      ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Uniforms</h1>
        <p className="mt-1 text-sm text-gray-500">{office ? "Family orders, the uniform stock and its suppliers. Money is received and refunded by the bursar." : "Order school uniform for your children, pay at the bursar's office, and collect it from the uniform office."}</p>
      </div>

      <div className={`grid gap-3 sm:grid-cols-2 ${office ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {tiles.map((tile) => (
          <button key={tile.label} type="button" onClick={tile.go} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}><tile.icon className="h-5 w-5" /></span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Uniform sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => (
          <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} onClick={() => go(item.id)} className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${tab === item.id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}>
            <item.icon className="h-4 w-4" /> {item.label}
            {item.badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{item.badge}</span> : null}
          </button>
        ))}
      </nav>

      {tab === "shop" && <CataloguePanel rows={items.data} setup={setup} isLoading={items.isLoading} isError={items.isError} onRetry={() => items.refetch()} onChanged={refresh} />}
      {tab === "orders" && <OrdersPanel rows={orders.data} office={office} setup={setup} isLoading={orders.isLoading} isError={orders.isError} onRetry={() => orders.refetch()} onChanged={refresh} initialView={view} />}
      {tab === "stock" && <StockPanel rows={items.data} setup={setup} isLoading={items.isLoading} isError={items.isError} onRetry={() => items.refetch()} onChanged={refresh} initialView={view} />}
      {tab === "history" && <HistoryPanel rows={moves.data} isLoading={moves.isLoading} isError={moves.isError} onRetry={() => moves.refetch()} />}
      {tab === "suppliers" && <SuppliersPanel rows={suppliers.data} isLoading={suppliers.isLoading} isError={suppliers.isError} onRetry={() => suppliers.refetch()} onChanged={refresh} />}
      {tab === "payments" && <PaymentsPanel rows={payments.data} office={office} isLoading={payments.isLoading} isError={payments.isError} onRetry={() => payments.refetch()} />}
    </div>
  );
}
